/**
 * Map Explorer canvas — spec §F5.1 ("Leaflet map, zoomControl bottom-right, panes
 * rasterPane z350 < choroPane z400 < adminPane z420 < vectorPane z450").
 *
 * This component owns the single `L.Map` instance for the app: it creates it into the
 * mockup's `#map` div, keeps the basemap in sync with the §F1 store, and publishes the
 * instance through `MapContext` so children (hierarchy layers, status bar, and from M2
 * the legend/dock/timebar) can attach to it. Children render only once the map exists.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import L from 'leaflet';

import { useAppStore } from '../../lib/state';
import { resolveBasemap, useBasemaps } from './basemaps';
import { MapContext } from './mapContext';
import { createPanes, fitTo, HOME_BOUNDS, MAX_ZOOM, MIN_ZOOM } from './panes';

declare global {
  interface Window {
    /** e2e handle (§F5.11 assertions need the real map view); read-only by convention. */
    __map?: L.Map;
  }
}

export interface MapViewProps {
  children?: ReactNode;
}

export function MapView({ children }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const baseRef = useRef<L.TileLayer | null>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const basemap = useAppStore((s) => s.basemap);
  const basemaps = useBasemaps();

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const m = L.map(el, {
      zoomControl: false,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      attributionControl: true,
    });
    L.control.zoom({ position: 'bottomright' }).addTo(m);
    m.attributionControl.setPrefix('Soil Values Geoportal');
    createPanes(m);
    fitTo(m, HOME_BOUNDS, false);

    mapRef.current = m;
    window.__map = m;
    setMap(m);

    return () => {
      m.remove();
      mapRef.current = null;
      baseRef.current = null;
      delete window.__map;
      setMap(null);
    };
  }, []);

  /**
   * Keep Leaflet's idea of the container size true (§F5.1).
   *
   * Leaflet 1.9 caches the container size in `map._size` and only ever refreshes it on
   * `window.resize`. Nothing in this app resizes the window: `#view-map` is a flexbox,
   * `#mapwrap` is `flex:1`, and `#sidebar.collapsed` sets `margin-left:-332px` — so
   * collapsing the sidebar makes the map **332 px wider** with no event Leaflet listens
   * for. The consequences are not cosmetic:
   *
   * * the newly-exposed strip is never filled, because `_getTiledPixelBounds` is computed
   *   from the stale size — a visibly empty band down one edge;
   * * `map.getSize().x` is wrong, and `Swipe.tsx` uses it for the handle clamp, the
   *   `inset()` it writes on the panes and its `aria-valuemax`, so the swipe handle can
   *   sit outside the map it is dividing;
   * * `latLngToContainerPoint` — the AOI clip's projection (`AoiClip.ts`) — is off by the
   *   same amount.
   *
   * A `ResizeObserver` on the container is the general fix: it fires for the sidebar
   * collapse, for the M4 analysis dock, for a devtools split and for a CSS change nobody
   * has thought of yet, which a `transitionend` hook on one specific element would not.
   * `animate:false` because the container has already moved — animating the map's
   * recentre to catch up would show the reader a pan they did not ask for.
   *
   * The first callback fires immediately on `observe()` with the current size; that one is
   * skipped, because the map was created against exactly that container a moment ago and
   * `invalidateSize` would fire a spurious `resize`/`moveend` before the first paint.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!map || !el || typeof ResizeObserver === 'undefined') return;
    let first = true;
    const observer = new ResizeObserver(() => {
      if (first) {
        first = false;
        return;
      }
      map.invalidateSize({ animate: false });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [map]);

  // Basemap swap (§F5.1 / §F1 `basemap`): remove the old tile layer, add the new one and
  // push it under every thematic pane.
  //
  // `basemaps` is a dependency because the catalogue arrives *after* the first paint: the
  // map is created before `/meta/config` answers, so it renders on the built-in fallback and
  // this effect re-runs once the served list lands. On a keyless deployment the two lists are
  // identical and the swap is invisible; on a keyed one it is what puts the CARTO key into
  // the tile URL.
  //
  // `maxZoom` is the provider's own limit, served with the row. It never binds — §F5.1 caps
  // the map at 12 and the shallowest provider here publishes to 17 — but a `TileLayer` that
  // knows its provider's limit is the correct object. **No `detectRetina`**: Leaflet expands
  // the `{r}` in CARTO's template to `@2x` from `Browser.retina` alone, and the option
  // additionally halves the tile size and adds a zoom offset, which would quadruple the tile
  // requests for a change nobody asked for.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (baseRef.current) m.removeLayer(baseRef.current);
    const def = resolveBasemap(basemaps, basemap);
    baseRef.current = L.tileLayer(def.url, {
      attribution: def.attr,
      maxZoom: def.maxZoom,
    }).addTo(m);
    baseRef.current.bringToBack();
  }, [basemap, basemaps, map]);

  return (
    <div id="mapwrap">
      <div id="map" data-testid="map-canvas" ref={containerRef} />
      {map ? <MapContext.Provider value={map}>{children}</MapContext.Provider> : null}
    </div>
  );
}

export default MapView;
